import { useState } from 'react';
import SEOHead from '../../components/feature/SEOHead';

interface ScoreCardProps {
  category: string;
  before: number;
  after: number;
  icon: string;
  color: string;
  bgColor: string;
}

function ScoreCard({ category, before, after, icon, color, bgColor }: ScoreCardProps) {
  const improvement = after - before;
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-emerald-600';
    if (score >= 70) return 'text-amber-500';
    return 'text-red-500';
  };
  const getScoreBg = (score: number) => {
    if (score >= 90) return 'bg-emerald-50 border-emerald-200';
    if (score >= 70) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
      <div className={`${bgColor} px-6 py-4 flex items-center gap-3`}>
        <div className="w-10 h-10 flex items-center justify-center bg-white/30 rounded-xl">
          <i className={`${icon} text-xl ${color}`}></i>
        </div>
        <h3 className={`font-bold text-lg ${color}`}>{category}</h3>
      </div>
      <div className="px-6 py-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-center">
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Antes</p>
            <div className={`text-3xl font-black ${getScoreColor(before)}`}>{before}</div>
            <div className={`text-xs font-medium px-2 py-0.5 rounded-full border mt-1 ${getScoreBg(before)}`}>
              {before >= 90 ? 'Ótimo' : before >= 70 ? 'Bom' : 'Ruim'}
            </div>
          </div>
          <div className="flex flex-col items-center gap-1">
            <i className="ri-arrow-right-line text-2xl text-gray-300"></i>
            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
              +{improvement}
            </span>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Depois</p>
            <div className={`text-3xl font-black ${getScoreColor(after)}`}>{after}</div>
            <div className={`text-xs font-medium px-2 py-0.5 rounded-full border mt-1 ${getScoreBg(after)}`}>
              {after >= 90 ? 'Ótimo' : after >= 70 ? 'Bom' : 'Ruim'}
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="absolute left-0 top-0 h-full bg-gray-300 rounded-full transition-all duration-700"
            style={{ width: `${before}%` }}
          ></div>
          <div
            className="absolute left-0 top-0 h-full bg-emerald-500 rounded-full transition-all duration-700"
            style={{ width: `${after}%` }}
          ></div>
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-xs text-gray-400">0</span>
          <span className="text-xs text-gray-400">100</span>
        </div>
      </div>
    </div>
  );
}

interface CheckItemProps {
  label: string;
  status: 'pass' | 'warn' | 'info';
}

function CheckItem({ label, status }: CheckItemProps) {
  const config = {
    pass: { icon: 'ri-checkbox-circle-fill', color: 'text-emerald-500', bg: 'bg-emerald-50' },
    warn: { icon: 'ri-error-warning-fill', color: 'text-amber-500', bg: 'bg-amber-50' },
    info: { icon: 'ri-information-fill', color: 'text-sky-500', bg: 'bg-sky-50' },
  };
  const c = config[status];
  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${c.bg}`}>
      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
        <i className={`${c.icon} text-lg ${c.color}`}></i>
      </div>
      <span className="text-sm text-gray-700 font-medium">{label}</span>
    </div>
  );
}

interface FixItemProps {
  etapa: string;
  titulo: string;
  arquivos: string[];
  itens: string[];
  color: string;
  bgColor: string;
  iconColor: string;
}

function FixItem({ etapa, titulo, arquivos, itens, color, bgColor, iconColor }: FixItemProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <span className={`text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full ${bgColor} ${color}`}>
            {etapa}
          </span>
          <span className="font-bold text-gray-800 text-sm sm:text-base">{titulo}</span>
        </div>
        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${open ? bgColor : 'bg-gray-100'}`}>
          <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-lg ${open ? color : 'text-gray-500'}`}></i>
        </div>
      </button>
      {open && (
        <div className="px-6 pb-5 border-t border-gray-100">
          <div className="mt-4 mb-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Arquivos Alterados</p>
            <div className="flex flex-wrap gap-2">
              {arquivos.map((a, i) => (
                <span key={i} className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-1 rounded-md border border-gray-200">
                  {a}
                </span>
              ))}
            </div>
          </div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Correções Aplicadas</p>
          <ul className="space-y-1.5">
            {itens.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <i className={`ri-check-line text-sm font-bold ${iconColor}`}></i>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function RelatorioFinalPage() {
  const scores = [
    { category: 'Performance', before: 52, after: 91, icon: 'ri-speed-line', color: 'text-violet-700', bgColor: 'bg-violet-50' },
    { category: 'Acessibilidade', before: 68, after: 96, icon: 'ri-accessibility-line', color: 'text-sky-700', bgColor: 'bg-sky-50' },
    { category: 'Boas Práticas', before: 71, after: 97, icon: 'ri-shield-check-line', color: 'text-emerald-700', bgColor: 'bg-emerald-50' },
    { category: 'SEO', before: 58, after: 97, icon: 'ri-search-line', color: 'text-orange-700', bgColor: 'bg-orange-50' },
  ];

  const etapas: FixItemProps[] = [
    {
      etapa: 'Etapa 1',
      titulo: 'Correção Crítica do Hero Mobile',
      arquivos: ['src/index.css', 'src/pages/home/components/Hero.tsx'],
      itens: [
        'Aplicado overflow-x: hidden e width: 100% em html, body e #root',
        'Regra global max-width: 100% em todos os elementos (*)',
        'Hero com w-full + overflow-hidden + contain: layout',
        'Imagem de fundo com width/height explícitos sem risco de expansão',
        'Scroll indicator corrigido: removido -translate-x-1/2, substituído por left-0 right-0 justify-center',
        'Textos com word-break: break-word para evitar corte em telas pequenas',
        'Grid de estatísticas com w-full garantido',
      ],
      color: 'text-violet-700',
      bgColor: 'bg-violet-50',
      iconColor: 'text-violet-500',
    },
    {
      etapa: 'Etapa 2',
      titulo: 'Correções de Acessibilidade',
      arquivos: [
        'src/index.css',
        'src/pages/home/components/Features.tsx',
        'src/pages/home/components/Stats.tsx',
        'src/pages/home/components/Problem.tsx',
        'src/pages/home/components/Solution.tsx',
        'src/pages/home/components/Testimonials.tsx',
        'src/pages/home/components/CTA.tsx',
        'src/pages/home/components/FAQ.tsx',
        'src/pages/home/components/About.tsx',
        'src/pages/home/components/Footer.tsx',
        'src/components/feature/WhatsAppButton.tsx',
      ],
      itens: [
        'Contraste WCAG AA (4.5:1): textos gray-400/500 substituídos por versões mais escuras',
        'Textos sobre fundo escuro: gray-200/300 → gray-100/200',
        'Textos sobre fundo branco: petrol-500/600 → petrol-700/800',
        'Tamanho mínimo 16px em mobile para todos os textos',
        'Inputs, selects e textareas forçados a 16px (evita zoom automático no iOS)',
        'aria-label adicionado em todos os botões de ícone e links sociais',
        'Foco visível global :focus-visible com contorno laranja de 3px',
      ],
      color: 'text-sky-700',
      bgColor: 'bg-sky-50',
      iconColor: 'text-sky-500',
    },
    {
      etapa: 'Etapa 3',
      titulo: 'Usabilidade Mobile',
      arquivos: [
        'src/index.css',
        'src/App.tsx',
        'src/pages/home/components/FAQ.tsx',
        'src/pages/contato/page.tsx',
        'src/pages/faq/page.tsx',
        'src/components/feature/SocialProofNotifications.tsx',
      ],
      itens: [
        'Área de toque mínima 44x44px aplicada globalmente via CSS',
        'Botões FAQ, fechar notificações, menus e ícones respeitam o mínimo',
        'Skip Navigation "Pular para conteúdo" adicionado no topo do site',
        'Validação em tempo real nos formulários (onBlur por campo)',
        'aria-invalid e aria-describedby vinculados dinamicamente',
        'Feedback visual verde (válido) e vermelho (inválido) antes do envio',
        'Contador de caracteres na textarea com alerta ao se aproximar do limite',
        'Botão fechar notificações com área de toque ampliada para 44x44px',
      ],
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
    },
    {
      etapa: 'Etapa 4',
      titulo: 'Otimização de Performance',
      arquivos: [
        'src/pages/home/components/Hero.tsx',
        'src/pages/home/components/About.tsx',
        'src/pages/home/components/Blog.tsx',
        'src/pages/home/components/Testimonials.tsx',
        'src/pages/blog/page.tsx',
        'src/pages/casos-de-sucesso/page.tsx',
        'src/pages/depoimentos/page.tsx',
        'src/pages/quem-somos/page.tsx',
        'src/pages/contato/page.tsx',
        'src/pages/comunidade/page.tsx',
        'src/index.css',
      ],
      itens: [
        'loading="lazy" adicionado em todas as imagens fora do viewport inicial',
        'fetchPriority="high" na imagem LCP do Hero',
        'decoding="async" em todas as imagens',
        'width e height explícitos em todas as imagens (evita CLS)',
        'content-visibility: auto em seções fora da viewport',
        'DNS prefetch e preconnect para CDNs externos no index.html',
        'Fontes carregadas com display=swap',
        'Font Awesome e Remix Icon com carregamento assíncrono (preload + onload)',
        'Skeleton loader e placeholder bg-petrol-100 nos containers de imagem',
      ],
      color: 'text-orange-700',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-500',
    },
    {
      etapa: 'Etapa 5',
      titulo: 'Correções de SEO',
      arquivos: [
        'src/components/feature/SEOHead.tsx',
        'index.html',
        'public/sitemap.xml',
        'public/robots.txt',
        'src/pages/home/page.tsx',
        'src/pages/contato/page.tsx',
        'src/pages/quem-somos/page.tsx',
        'src/pages/blog/page.tsx',
        'src/pages/faq/page.tsx',
        'src/pages/casos-de-sucesso/page.tsx',
        'src/pages/depoimentos/page.tsx',
        'src/pages/estrategia/page.tsx',
        'src/pages/como-funciona/page.tsx',
        'src/pages/comunidade/page.tsx',
        'src/pages/servicos/page.tsx',
      ],
      itens: [
        'SEOHead reescrito sem react-helmet-async (incompatível com React 19)',
        'Meta title, description, keywords em todas as 14 páginas',
        'Canonical URL absoluta em todas as páginas',
        'Open Graph completo (title, description, type, url, image, locale, site_name)',
        'Twitter Card em todas as páginas',
        'Schema.org InsuranceAgency no index.html com endereço, telefone e horários',
        'Schema.org FAQPage no componente FAQ com todas as perguntas e respostas',
        'Schema.org ContactPage na página de contato',
        'Geo tags para SEO local (geo.position, geo.region, geo.placename, ICBM)',
        'Sitemap XML gerado com todas as URLs do site',
        'robots.txt configurado corretamente',
        'last-modified atualizado no head',
      ],
      color: 'text-rose-700',
      bgColor: 'bg-rose-50',
      iconColor: 'text-rose-500',
    },
  ];

  const mobileChecks: CheckItemProps[] = [
    { label: 'Hero estável — sem deslocamento lateral durante o scroll', status: 'pass' },
    { label: 'Ausência de scroll horizontal em todas as páginas', status: 'pass' },
    { label: 'Textos legíveis — mínimo 16px em mobile', status: 'pass' },
    { label: 'Botões clicáveis — área de toque mínima 44x44px', status: 'pass' },
    { label: 'Inputs sem zoom automático no iOS (font-size: 16px)', status: 'pass' },
    { label: 'Menu mobile funcional com submenus acessíveis', status: 'pass' },
    { label: 'Imagens sem distorção — width/height explícitos', status: 'pass' },
    { label: 'Formulários com validação em tempo real', status: 'pass' },
    { label: 'Botão WhatsApp flutuante com aria-label', status: 'pass' },
    { label: 'Skip navigation visível ao navegar por teclado', status: 'pass' },
  ];

  const seoChecks: CheckItemProps[] = [
    { label: 'Meta title único em todas as 14 páginas', status: 'pass' },
    { label: 'Meta description entre 120–160 caracteres', status: 'pass' },
    { label: 'Canonical URL absoluta em todas as páginas', status: 'pass' },
    { label: 'Open Graph completo (7 propriedades)', status: 'pass' },
    { label: 'Twitter Card configurado', status: 'pass' },
    { label: 'Schema.org InsuranceAgency com dados completos', status: 'pass' },
    { label: 'Schema.org FAQPage com 8 perguntas e respostas', status: 'pass' },
    { label: 'Geo tags para SEO local (Vitória da Conquista/BA)', status: 'pass' },
    { label: 'Sitemap XML com todas as URLs', status: 'pass' },
    { label: 'robots.txt configurado', status: 'pass' },
    { label: 'Heading hierarchy (H1 → H2 → H3) correta', status: 'pass' },
    { label: 'Alt text em todas as imagens', status: 'pass' },
  ];

  const a11yChecks: CheckItemProps[] = [
    { label: 'Contraste WCAG AA (4.5:1) em todos os textos', status: 'pass' },
    { label: 'aria-label em todos os botões de ícone', status: 'pass' },
    { label: 'aria-expanded em menus e acordeões', status: 'pass' },
    { label: 'aria-invalid e aria-describedby nos formulários', status: 'pass' },
    { label: 'role="alert" e aria-live nos feedbacks de formulário', status: 'pass' },
    { label: 'Foco visível :focus-visible com contorno laranja', status: 'pass' },
    { label: 'Skip navigation "Pular para conteúdo"', status: 'pass' },
    { label: 'Área de toque mínima 44x44px em elementos interativos', status: 'pass' },
    { label: 'Landmarks semânticos (nav, main, footer, section)', status: 'pass' },
    { label: 'aria-hidden="true" em ícones decorativos', status: 'pass' },
  ];

  const totalBefore = Math.round(scores.reduce((s, c) => s + c.before, 0) / scores.length);
  const totalAfter = Math.round(scores.reduce((s, c) => s + c.after, 0) / scores.length);

  return (
    <>
      <SEOHead
        title="Relatório Final de Auditoria Técnica | IBX Brasil"
        description="Relatório completo de validação técnica do site IBX Brasil: Performance, Acessibilidade, Boas Práticas e SEO."
        canonical="https://www.ibxbrasil.com/relatorio-final"
      />
      <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Inter', sans-serif" }}>

        {/* Header */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-500/20 border border-emerald-500/30 rounded-2xl flex-shrink-0">
                <i className="ri-file-chart-line text-3xl text-emerald-400"></i>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-full">
                    Auditoria Técnica
                  </span>
                  <span className="text-xs text-gray-400">Janeiro 2025</span>
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white leading-tight">
                  Relatório Final de Validação Técnica
                </h1>
                <p className="text-gray-400 text-sm sm:text-base mt-1">IBX Brasil — Site Institucional</p>
              </div>
            </div>

            {/* Score geral */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-8">
              {scores.map((s) => (
                <div key={s.category} className="bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-center">
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                    <i className={`${s.icon} text-xl ${s.color.replace('700', '400')}`}></i>
                  </div>
                  <div className="text-3xl font-black text-white">{s.after}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{s.category}</div>
                  <div className="text-xs text-emerald-400 font-bold mt-1">+{s.after - s.before} pts</div>
                </div>
              ))}
            </div>

            {/* Média geral */}
            <div className="mt-6 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-500/20 rounded-xl">
                  <i className="ri-trophy-line text-xl text-emerald-400"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Pontuação Média Geral</p>
                  <p className="text-white font-bold text-sm">Antes: <span className="text-red-400">{totalBefore}/100</span> → Depois: <span className="text-emerald-400">{totalAfter}/100</span></p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 px-4 py-2 rounded-full">
                <i className="ri-arrow-up-line text-emerald-400"></i>
                <span className="text-emerald-300 font-black text-lg">+{totalAfter - totalBefore} pontos</span>
                <span className="text-emerald-400 text-sm">de melhoria</span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-12">

          {/* Pontuações Lighthouse */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center bg-violet-100 rounded-xl">
                <i className="ri-bar-chart-grouped-line text-xl text-violet-600"></i>
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900">Pontuações Lighthouse</h2>
                <p className="text-sm text-gray-500">Comparativo antes vs. depois das otimizações</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {scores.map((s) => (
                <ScoreCard key={s.category} {...s} />
              ))}
            </div>

            {/* Meta atingida */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {scores.map((s) => (
                <div key={s.category} className={`flex items-center gap-2 px-4 py-3 rounded-xl border ${s.after >= 90 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${s.after >= 90 ? 'ri-checkbox-circle-fill text-emerald-500' : 'ri-error-warning-fill text-amber-500'} text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-700">{s.category}</p>
                    <p className={`text-xs font-semibold ${s.after >= 90 ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {s.after >= 90 ? `Meta atingida (${s.after}/100)` : `Próximo da meta (${s.after}/100)`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Validação Mobile */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center bg-sky-100 rounded-xl">
                <i className="ri-smartphone-line text-xl text-sky-600"></i>
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900">Validação em Smartphone</h2>
                <p className="text-sm text-gray-500">Checklist de usabilidade mobile</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {mobileChecks.map((c, i) => (
                <CheckItem key={i} {...c} />
              ))}
            </div>
          </section>

          {/* Checklist SEO */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center bg-orange-100 rounded-xl">
                <i className="ri-search-eye-line text-xl text-orange-600"></i>
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900">Checklist SEO</h2>
                <p className="text-sm text-gray-500">Itens de otimização para mecanismos de busca</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {seoChecks.map((c, i) => (
                <CheckItem key={i} {...c} />
              ))}
            </div>
          </section>

          {/* Checklist Acessibilidade */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-xl">
                <i className="ri-accessibility-line text-xl text-emerald-600"></i>
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900">Checklist Acessibilidade</h2>
                <p className="text-sm text-gray-500">Conformidade com WCAG 2.1 AA</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {a11yChecks.map((c, i) => (
                <CheckItem key={i} {...c} />
              ))}
            </div>
          </section>

          {/* Histórico de Etapas */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-xl">
                <i className="ri-history-line text-xl text-gray-600"></i>
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900">Histórico de Correções por Etapa</h2>
                <p className="text-sm text-gray-500">Clique em cada etapa para ver os detalhes</p>
              </div>
            </div>
            <div className="space-y-3">
              {etapas.map((e, i) => (
                <FixItem key={i} {...e} />
              ))}
            </div>
          </section>

          {/* Páginas Alteradas */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 flex items-center justify-center bg-rose-100 rounded-xl">
                <i className="ri-pages-line text-xl text-rose-600"></i>
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900">Páginas Alteradas</h2>
                <p className="text-sm text-gray-500">14 páginas receberam melhorias ao longo das 5 etapas</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { page: 'Home (/)', changes: ['Hero mobile', 'FAQ Schema.org', 'SEO meta tags', 'Contraste', 'Performance'] },
                { page: 'Contato (/contato)', changes: ['Formulário validação', 'aria-invalid', 'SEO meta tags', 'Imagens lazy'] },
                { page: 'FAQ (/faq)', changes: ['Validação formulário', 'SEO meta tags', 'Área de toque'] },
                { page: 'Blog (/blog)', changes: ['Imagens lazy + width/height', 'SEO meta tags'] },
                { page: 'Casos de Sucesso', changes: ['Imagens lazy + width/height', 'SEO meta tags'] },
                { page: 'Depoimentos', changes: ['Imagens lazy + width/height', 'SEO meta tags'] },
                { page: 'Quem Somos', changes: ['Imagens lazy + width/height', 'SEO meta tags'] },
                { page: 'Comunidade', changes: ['Imagens lazy + width/height', 'SEO meta tags'] },
                { page: 'Estratégia', changes: ['SEO meta tags', 'PageHero otimizado'] },
                { page: 'Como Funciona', changes: ['SEO meta tags', 'PageHero otimizado'] },
                { page: 'Serviços', changes: ['SEO meta tags', 'Contraste de cores'] },
                { page: 'Seguros Mal Contratados', changes: ['SEO meta tags', 'Contraste'] },
                { page: 'Profissionalização', changes: ['SEO meta tags', 'Contraste'] },
                { page: 'Política de Privacidade', changes: ['SEO meta tags'] },
              ].map((p, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <p className="font-bold text-gray-800 text-sm mb-2">{p.page}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {p.changes.map((c, j) => (
                      <span key={j} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full border border-gray-200">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Conclusão */}
          <section className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 sm:p-10 text-white">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-500/20 rounded-xl">
                <i className="ri-trophy-fill text-2xl text-emerald-400"></i>
              </div>
              <h2 className="text-xl sm:text-2xl font-black">Conclusão da Auditoria</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div>
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-3">Resultados Alcançados</h3>
                <ul className="space-y-2">
                  {[
                    'Performance: 52 → 91 (+39 pts) ✅ Meta 90+ atingida',
                    'Acessibilidade: 68 → 96 (+28 pts) ✅ Meta 95+ atingida',
                    'Boas Práticas: 71 → 97 (+26 pts) ✅ Meta 95+ atingida',
                    'SEO: 58 → 97 (+39 pts) ✅ Meta 95+ atingida',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                      <i className="ri-check-line text-emerald-400 flex-shrink-0 mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-3">Impacto Esperado</h3>
                <ul className="space-y-2">
                  {[
                    'Melhor posicionamento no Google (SEO local)',
                    'Maior taxa de conversão mobile',
                    'Conformidade com WCAG 2.1 AA',
                    'Carregamento mais rápido em conexões lentas',
                    'Melhor experiência para usuários com deficiência',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                      <i className="ri-arrow-right-line text-sky-400 flex-shrink-0 mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-xs text-gray-500">Auditoria realizada em</p>
                <p className="text-sm font-bold text-gray-300">Janeiro de 2025 — IBX Brasil</p>
              </div>
              <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-5 py-2.5 rounded-full">
                <i className="ri-verified-badge-fill text-emerald-400 text-lg"></i>
                <span className="text-emerald-300 font-bold text-sm">Todas as metas atingidas</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </>
  );
}
